import { FC, Fragment } from 'react';
import FleetTitle from '@/libs/antd/components/Title';
import {
	DeleteOutlined,
	EditOutlined,
	LoadingOutlined,
	UploadOutlined,
} from '@ant-design/icons';
import {
	Button,
	Checkbox,
	Col,
	DatePicker,
	Divider,
	Flex,
	Form,
	InputNumber,
	notification,
	Radio,
	Row,
	Select,
	Table,
	Typography,
	Upload,
	UploadFile,
} from 'antd';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import TextArea from 'antd/es/input/TextArea';
import { useModals } from '@/components/modals/ModalProvider';
import { useApp } from '@/App';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm } from '@/libs/antd/form';
import rule from '@/libs/antd/rules';
import {
	getSCRisalahDetail,
	SubmitRisalahPayload,
	submitSCRisalah,
} from '@/libs/fleet-api/services/nap';
import dayjs from 'dayjs';
import { FileData } from '@/libs/fleet-api/services/types';
import { uploader } from '@/libs/fleet-api/uploader';
import fetcher from '@/libs/fleet-api/fetcher';
import { getMasterData } from '@/libs/fleet-api/services/master-data';
import { inputCurrency } from '@/libs/antd/input-number';
import CovenantModal, {
	CovenantPayload,
	useCovenantModal,
} from './CovenantModal';
import { formatCurrency } from '@/libs/antd/formater';
import { NAPNJFModal, useNAPNJFModal } from '@/pages/nap/NAPDownloadPage';
import { NAPJFModal, useNAPJFModal } from '@/pages/nap/NAPJFDownloadPage';

type SCRisalahForm = SubmitRisalahPayload;

const SCRisalahPage: FC<{ prospectId: string; isReadOnly?: boolean }> = ({
	prospectId,
	isReadOnly,
}) => {
	// const prospectId = '943P2024120003';
	const { navigate } = useApp();
	const napNJFModal = useNAPNJFModal();
	const napJFModal = useNAPJFModal();
	const { modal, openCustomerDetail, openCustomerGroupDetail } = useModals();
	const covenantModal = useCovenantModal();

	const { data, isLoading, isFetching } = useQuery({
		queryKey: ['getSCRisalahDetail', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, prospectId] = queryKey;
			return getSCRisalahDetail(prospectId);
		},
		enabled: !!prospectId,
	});

	const { form, value, fieldName } = useForm<SCRisalahForm>();

	const submitRisalah = useMutation({ mutationFn: submitSCRisalah });

	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	const requestStatusQuery = useQuery({
		queryKey: ['getMasterData', 'request-status'],
		queryFn: async () => {
			const res = await getMasterData('request-status');
			return res.data.map((data) => {
				return {
					label: data.value,
					value: data.id,
					data,
				};
			});
		},
	});

	const {
		// napId,
		napNo,
		customerId,
		customerName,
		customerGroupName,
		customerGroupId,
		resolutionFile,
		napApprovalHistory,
		covenants,
		categoryOptions,
		priorityOptions,
		rkpDate,
		bodResolutionDate,
		bodResolutionFullApprovalDate,
		requestStatus,
		notes,
		repeatFrequencyOptions,
		customRepeatFrequencyOptions,
		monitoringType,
		ultimateOwnerName,
		facilityTypeOptions,
		facilityValidityPeriod,
		financingTypeOptions,
		financingObjectOptions,
		financingLimitTypeOptions,
		furtherAnalysisOptions,
		plafons,
		monthOptions,
		positionOptions,
		dayOfWeekOptions,
	} = data?.data ?? {};

	if (isFetching) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	return (
		<Fragment>
			<FleetTitle>Input dan Upload Risalah</FleetTitle>
			<NAPNJFModal {...napNJFModal} />
			<NAPJFModal {...napJFModal} />
			<CovenantModal
				{...covenantModal}
				customerId={customerId!}
				prospectId={prospectId}
				categoryOptions={categoryOptions!}
				furtherAnalysisOptions={furtherAnalysisOptions!}
				monitoringType={monitoringType!}
				priorityOptions={priorityOptions!}
				repeatFrequencyOptions={repeatFrequencyOptions!}
				customRepeatFrequencyOptions={customRepeatFrequencyOptions!}
				monthOptions={monthOptions!}
				positionOptions={positionOptions!}
				dayOfWeekOptions={dayOfWeekOptions!}
			/>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				onFinish={async (values) => {
					try {
						const payload = {
							...values,
							covenants: values.covenants?.map((covenant) => ({
								...covenant,
								dueDate: covenant.dueDate ?? null,
							})),
							plafons: values.plafons?.map((plafon) => {
								// Ensure only the required fields are sent, plafonType object is already excluded by type
								const {
									id,
									isDefault,
									plafonTypeId,
									financingTypesId,
									facilityTypesId,
									approvedLimit,
									financingLimitTypeId,
									financingObjectsId,
									requestedLimit,
								} = plafon;
								return {
									id,
									isDefault,
									plafonTypeId,
									financingTypesId,
									facilityTypesId,
									approvedLimit,
									financingLimitTypeId,
									financingObjectsId,
									requestedLimit,
								};
							}),
							napNo: napNo!,
						};

						const { data } = await submitRisalah.mutateAsync(payload);
						if (data) {
							await modal.success({
								title: (
									<Fragment>
										<small>Sukses</small>
									</Fragment>
								),
							});
							navigate('SCRisalahListPage');
						}
					} catch (error) {
						if (fetcher.isError(error)) {
							const message = await error.json();
							notification.error({
								duration: 20,
								message: `Error ID: ${message?.metadata?.debugInfo?.errorId ?? 'Unknown'}`,
								description: (
									<div>
										{message?.metadata?.debugInfo?.messages?.length ? (
											<ul>
												{message?.metadata?.debugInfo?.messages.map(
													(msg: string) => <li key={msg}>{msg}</li>,
												)}
											</ul>
										) : (
											<pre>Opps, Something went wrong</pre>
										)}
									</div>
								),
							});
						}
					}
				}}
			>
				<div style={{ height: 20 }}></div>

				<Row gutter={[20, 20]}>
					<Col span={4}>
						<Typography.Text>
							<strong style={{ fontSize: 14 }}>Nama Customer</strong>
						</Typography.Text>
					</Col>
					<Col span={8}>
						<a
							href="#customer-detail"
							className="trigger-popup"
							onClick={(e) => {
								e.preventDefault();
								openCustomerDetail(customerId!);
							}}
						>
							{customerName}
						</a>
					</Col>
					<Col span={4}>
						<Typography.Text>
							<strong style={{ fontSize: 14 }}>NAP ID</strong>
						</Typography.Text>
					</Col>
					<Col span={8}>
						<a
							href="#"
							onClick={(e) => {
								e.preventDefault();
								if (!prospectId) return;

								navigate('NAPPage', {
									prospectId,
									onBack: () => navigate('SCRisalahPage', { prospectId }),
								});
							}}
						>
							{napNo}
						</a>
					</Col>
				</Row>
				<Divider />
				<Row gutter={[20, 20]}>
					<Col span={4}>
						<Typography.Text>
							<strong style={{ fontSize: 14 }}>Group BMPP</strong>
						</Typography.Text>
					</Col>
					<Col span={8}>
						<a
							href="#customer-detail"
							className="trigger-popup"
							onClick={(e) => {
								e.preventDefault();
								if (customerGroupId) {
									openCustomerGroupDetail(customerGroupId);
								}
							}}
						>
							{customerGroupName}
						</a>
					</Col>
					<Col span={4}>
						<Typography.Text>
							<strong style={{ fontSize: 14 }}>Summary NAP</strong>
						</Typography.Text>
					</Col>
					<Col span={8}>
						<a
							href="#"
							onClick={(e) => {
								e.preventDefault();
								const plafonType = plafons?.[0]?.plafonType?.value;
								if (!plafonType) return;

								if (plafonType === 'Join Financing') {
									napJFModal.open({
										prospectId,
									});
								} else {
									napNJFModal.open({
										prospectId,
									});
								}
							}}
						>
							View Dokumen
						</a>
					</Col>
				</Row>
				<Divider />
				<Row gutter={[20, 20]}>
					<Col span={12}>
						<Form.Item
							label="Ultimate Owner"
							labelCol={{ span: 8 }}
						>
							<Typography.Text>{ultimateOwnerName ?? '-'}</Typography.Text>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name={fieldName('resolutionFile')}
							initialValue={resolutionFile}
							label="Upload Risalah"
							rules={[rule.required()]}
							labelCol={{ span: 8 }}
							getValueProps={(value) => {
								const file = value as FileData;
								return {
									fileList: file?.id
										? ([
												{
													uid: file.id,
													name: file.name,
													thumbUrl: file.url,
												},
											] as UploadFile[])
										: [],
								};
							}}
							normalize={({ file }: { file: UploadFile }) => {
								return {
									id: file.uid,
									format: file.type,
									name: file.name,
									url: file.thumbUrl,
								} as FileData;
							}}
						>
							<Upload.Dragger
								disabled={isReadOnly}
								listType="picture"
								maxCount={1}
								beforeUpload={(file) => {
									const isPDF =
										file.type === 'application/pdf' ||
										file.name.toLowerCase().endsWith('.pdf');
									if (!isPDF) {
										notification.error({
											message: 'Invalid File Type',
											description:
												'Hanya file PDF yang diperbolehkan untuk upload Risalah.',
											duration: 5,
										});
										return Upload.LIST_IGNORE;
									}
									return false;
								}}
								onRemove={() => {
									form.setFieldValue(fieldName('resolutionFile'), null);
									return false;
								}}
								onChange={async ({ file }) => {
									const fileid = Date.now().toString();
									const filepath = `customer/${customerId}/${prospectId}/nap/risalah`;

									try {
										const url = await uploadMutation.mutateAsync({
											file: file as unknown as File,
											fileid,
											filepath,
										});

										form.setFieldValue(fieldName('resolutionFile'), {
											format: 'jpeg',
											id: fileid,
											name: file.name,
											path: filepath,
											url,
										});
									} catch {
										form.setFieldValue(fieldName('resolutionFile'), null);
									}
								}}
							>
								<UploadOutlined
									style={{
										fontSize: 25,
										color: '#BABABA',
										display: 'block',
									}}
								/>
							</Upload.Dragger>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={[20, 20]}>
					<Col span={12}>
						<Form.Item
							name={fieldName('rkpDate')}
							initialValue={rkpDate}
							label="Tanggal RKP"
							rules={[rule.required()]}
							labelCol={{ span: 8 }}
							getValueProps={(value) => ({
								value: value && dayjs(value),
							})}
							normalize={(value) =>
								value && dayjs(value).add(7, 'hour').utc(true).toISOString()
							}
						>
							<DatePicker disabled={isReadOnly} />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name={fieldName('bodResolutionDate')}
							initialValue={bodResolutionDate}
							label="Tanggal Risalah to BOD"
							rules={[rule.required()]}
							labelCol={{ span: 8 }}
							getValueProps={(value) => ({
								value: value && dayjs(value),
							})}
							normalize={(value) =>
								value && dayjs(value).add(7, 'hour').utc(true).toISOString()
							}
						>
							<DatePicker disabled={isReadOnly} />
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={[20, 20]}>
					<Col span={12}>
						<Form.Item
							name={fieldName('bodResolutionFullApprovalDate')}
							initialValue={bodResolutionFullApprovalDate}
							label="Tanggal Risalah Full Approved"
							rules={[rule.required()]}
							labelCol={{ span: 8 }}
							getValueProps={(value) => ({
								value: value && dayjs(value),
							})}
							normalize={(value) =>
								value && dayjs(value).add(7, 'hour').utc(true).toISOString()
							}
						>
							<DatePicker disabled={isReadOnly} />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name={fieldName('facilityValidityPeriod')}
							initialValue={facilityValidityPeriod}
							label="Masa Berlaku Fasilitas"
							rules={[rule.required()]}
							labelCol={{ span: 8 }}
							getValueProps={(value) => ({
								value: value && dayjs(value),
							})}
							normalize={(value) =>
								value && dayjs(value).add(7, 'hour').utc(true).toISOString()
							}
						>
							<DatePicker disabled={isReadOnly} />
						</Form.Item>
					</Col>
				</Row>

				<Divider />
				<Form.Item
					name={fieldName('requestStatus')}
					initialValue={requestStatus?.id}
					label="Status Pengajuan"
					labelCol={{ span: 4 }}
					wrapperCol={{ span: 10 }}
				>
					<Radio.Group style={{ marginTop: 10 }}>
						{requestStatusQuery.data?.map((status) => (
							<Radio
								key={status.value}
								value={status.value}
								disabled={isReadOnly}
							>
								{status.label}
							</Radio>
						))}
					</Radio.Group>
				</Form.Item>

				<Form.List
					name={fieldName('plafons')}
					initialValue={
						plafons?.map((plafon) => {
							return {
								approvedLimit: plafon.approvedLimit,
								facilityTypesId: plafon.facilityTypesId,
								financingLimitTypeId: plafon.financingLimitType.id,
								financingObjectsId: plafon.financingObjectsId,
								financingTypesId: plafon.financingTypesId,
								id: plafon.id,
								isDefault: plafon.isDefault,
								plafonTypeId: plafon.plafonType.id,
								requestedLimit: plafon.requestedLimit,
							} as SCRisalahForm['plafons'][number];
						}) ?? []
					}
				>
					{(fields, control) => {
						return (
							<Fragment>
								{fields.map((field, index) => {
									const record = value?.plafons?.[index];

									return (
										<Fragment key={field.key}>
											<Divider />
											{form.getFieldValue(fieldName('requestStatus')) !== 2 ? (
												<Fragment>
													<Form.Item
														name={[field.name, 'isDefault']}
														noStyle
													/>
													<Row wrap={false}>
														<Col flex="auto">
															<Typography.Title
																level={3}
																style={{ marginTop: 0 }}
															>
																Limit Plafon {index + 1}
															</Typography.Title>
														</Col>
														<Col flex="none">
															<Button
																danger
																style={{
																	borderRadius: 50,
																	fontSize: 12,
																	height: 24,
																	fontWeight: 700,
																}}
																disabled={record?.isDefault || isReadOnly}
																onClick={async () => {
																	const isConfirmed = await modal.confirm({
																		title:
																			'Apakah Anda yakin ingin menghapus plafon ini?',
																		okText: 'YA',
																		cancelText: 'TIDAK',
																	});
																	if (isConfirmed) control.remove(index);
																}}
															>
																Hapus Plafond
															</Button>
														</Col>
													</Row>
													<Form.Item
														name={[field.name, 'financingTypesId']}
														label="Jenis Pembiayaan"
														rules={[rule.required()]}
														labelCol={{ span: 4 }}
														wrapperCol={{ span: 20 }}
													>
														<Checkbox.Group style={{ width: '100%' }}>
															<Row gutter={[8, 8]}>
																{financingTypeOptions?.map((option, i) => (
																	<Col
																		span={12}
																		key={i}
																	>
																		<Checkbox
																			value={option.id}
																			disabled={isReadOnly}
																		>
																			{option.value}
																		</Checkbox>
																	</Col>
																))}
															</Row>
														</Checkbox.Group>
													</Form.Item>
													<Divider />
													<Form.Item
														name={[field.name, 'facilityTypesId']}
														label="Jenis Fasilitas"
														rules={[rule.required()]}
														labelCol={{ span: 4 }}
														wrapperCol={{ span: 20 }}
													>
														<Checkbox.Group style={{ width: '100%' }}>
															<Row gutter={[8, 8]}>
																{facilityTypeOptions?.map((option, i) => (
																	<Col
																		span={12}
																		key={i}
																	>
																		<Checkbox
																			value={option.id}
																			disabled={isReadOnly}
																		>
																			{option.value}
																		</Checkbox>
																	</Col>
																))}
															</Row>
														</Checkbox.Group>
													</Form.Item>
													<Divider />
													<Row
														gutter={[20, 20]}
														align={'middle'}
													>
														<Col span={4}>
															<Typography.Text>
																<strong style={{ fontSize: 14 }}>
																	Limit Pengajuan
																</strong>
															</Typography.Text>
														</Col>
														<Col span={8}>
															<Typography.Text>
																{record?.requestedLimit
																	? formatCurrency(record.requestedLimit)
																	: '-'}
															</Typography.Text>
														</Col>
														<Col span={12}>
															<Form.Item
																name={[field.name, 'approvedLimit']}
																label="Limit Disetujui"
																rules={[rule.required()]}
																labelCol={{ span: 8 }}
																wrapperCol={{ span: 16 }}
															>
																<InputNumber
																	{...inputCurrency}
																	disabled={isReadOnly}
																/>
															</Form.Item>
														</Col>
													</Row>
													<Divider />
													<Row
														gutter={[20, 20]}
														align={'middle'}
													>
														<Col span={12}>
															<Form.Item
																name={[field.name, 'financingLimitTypeId']}
																label="Sifat Limit"
																rules={[rule.required()]}
																labelCol={{ span: 8 }}
																wrapperCol={{ span: 16 }}
															>
																<Select
																	options={financingLimitTypeOptions?.map(
																		(option) => {
																			return {
																				value: option.id,
																				label: option.value,
																			};
																		},
																	)}
																	disabled={isReadOnly}
																/>
															</Form.Item>
														</Col>
														<Col span={4}>
															<Typography.Text>
																<strong style={{ fontSize: 14 }}>
																	Jenis Plafon
																</strong>
															</Typography.Text>
														</Col>
														<Col span={8}>
															<Typography.Text>
																{record?.plafonTypeId === 1
																	? 'Non Join Financing'
																	: 'Join Financing'}
															</Typography.Text>
														</Col>
													</Row>
													<Divider />
													<Form.Item
														name={[field.name, 'financingObjectsId']}
														label="Objek Pembiayaan"
														rules={[rule.required()]}
														labelCol={{ span: 4 }}
														wrapperCol={{ span: 20 }}
													>
														<Checkbox.Group>
															<Flex
																gap={20}
																wrap="wrap"
															>
																{financingObjectOptions?.map((option, i) => (
																	<Checkbox
																		value={option.id}
																		key={i}
																		disabled={isReadOnly}
																	>
																		{option.value}
																	</Checkbox>
																))}
															</Flex>
														</Checkbox.Group>
													</Form.Item>
													{fields.length < 2 && (
														<Fragment>
															<Divider />
															<div
																style={{
																	display: 'flex',
																	justifyContent: 'flex-end',
																}}
															>
																<Button
																	style={{
																		borderRadius: 50,
																		fontSize: 12,
																		height: 24,
																		fontWeight: 700,
																		color: '#1557BC',
																		borderColor: '#1557BC',
																	}}
																	icon={'+'}
																	disabled={isReadOnly}
																	onClick={() =>
																		control.add({
																			plafonTypeId:
																				(
																					form.getFieldValue(
																						fieldName('plafons'),
																					)?.[0] as unknown as {
																						plafonTypeId: number;
																					}
																				)?.plafonTypeId === 1
																					? 2
																					: 1,
																			isDefault: false,
																		})
																	}
																>
																	Add Plafond
																</Button>
															</div>
														</Fragment>
													)}
												</Fragment>
											) : (
												<Fragment>
													<Form.Item
														name={[field.name, 'isDefault']}
														noStyle
													/>

													<Typography.Title
														level={3}
														style={{ marginTop: 0 }}
													>
														Limit Plafon {index + 1}
													</Typography.Title>
													<Row
														gutter={[20, 20]}
														align={'middle'}
													>
														<Col span={4}>
															<Typography.Text>
																<strong style={{ fontSize: 14 }}>
																	Limit Pengajuan
																</strong>
															</Typography.Text>
														</Col>
														<Col span={8}>
															<Typography.Text>
																{record?.requestedLimit
																	? formatCurrency(record.requestedLimit)
																	: '-'}
															</Typography.Text>
														</Col>
														<Col span={4}>
															<Typography.Text>
																<strong style={{ fontSize: 14 }}>
																	Jenis Plafon
																</strong>
															</Typography.Text>
														</Col>
														<Col span={8}>
															<Typography.Text>
																{record?.plafonTypeId === 1
																	? 'Non Join Financing'
																	: 'Join Financing'}
															</Typography.Text>
														</Col>
													</Row>
													<Divider />
												</Fragment>
											)}
										</Fragment>
									);
								})}
							</Fragment>
						);
					}}
				</Form.List>
				<div style={{ height: 20 }}></div>
				{form.getFieldValue(fieldName('requestStatus')) !== 2 && (
					<Fragment>
						<FleetTitle>List Ketentuan & Syarat Kredit</FleetTitle>

						<Row style={{ marginBlock: 20 }}>
							<Col flex="auto" />
							<Col flex="none">
								<Button
									style={{
										borderRadius: 50,
										fontSize: 12,
										height: 24,
										fontWeight: 700,
										...(!isReadOnly
											? {
													color: '#1557BC',
													borderColor: '#1557BC',
												}
											: {}),
									}}
									icon={'+'}
									disabled={isReadOnly}
									onClick={async () => {
										const newRecord = await covenantModal.open({
											type: 'new',
										});
										if (newRecord) {
											form.setFieldValue(fieldName('covenants'), [
												...(value?.covenants ?? []),
												newRecord,
											]);
										}
									}}
								>
									Add Another
								</Button>
							</Col>
						</Row>

						<Form.Item
							name={fieldName('covenants')}
							initialValue={covenants?.map((d) => {
								return {
									id: d?.id,
									categoryId: d.category?.id,
									covenant: d.covenant,
									covenantDocument: d.covenantDocument,
									dueDate: d.dueDate,
									furtherAnalysisId: d.furtherAnalysis?.id,
									monitoringTypeId: d.monitoringType?.id,
									priorityId: d.priority?.id,
									repeatFrequencyId: d.repeatFrequency?.id,
								} as CovenantPayload;
							})}
							noStyle
						>
							<Table
								bordered
								pagination={false}
								style={{ marginBottom: 20 }}
								dataSource={value?.covenants}
								columns={[
									{
										title: 'No',
										align: 'center',
										width: 100,
										render: (_, __, index) => index + 1,
									},
									{
										title: 'Action',
										hidden: isReadOnly,
										colSpan: 2,
										width: 100,
										align: 'center',
										render: (_, record, index) => (
											<a
												onClick={async (e) => {
													e.preventDefault();
													const editData = await covenantModal.open({
														type: 'edit',
														record,
													});
													if (editData) {
														form.setFieldValue(
															fieldName('covenants'),
															value?.covenants?.map((record, i) => {
																return index === i ? editData : record;
															}),
														);
													}
												}}
											>
												<EditOutlined />
											</a>
										),
									},
									{
										colSpan: 0,
										hidden: isReadOnly,
										width: 100,
										align: 'center',
										render: (_, __, index) => (
											<a
												onClick={async (e) => {
													e.preventDefault();
													const isConfirmed = await modal.confirm({
														title:
															'Apakah Anda yakin ingin menghapus data ini?',
														okText: 'YA',
														cancelText: 'TIDAK',
													});

													if (!isConfirmed) return;

													form.setFieldValue(
														fieldName('covenants'),
														(value?.covenants ?? []).filter(
															(_, i) => index !== i,
														),
													);
												}}
											>
												<DeleteOutlined />
											</a>
										),
									},
									{
										title: 'Covenant',
										render: (_, record) => record?.covenant,
									},
									{
										title: 'Kategori',
										render: (_, record) =>
											record?.categoryId
												? categoryOptions?.find(
														(data) => data.id === record.categoryId,
													)?.value
												: '-',
									},
									{
										title: 'Priority',
										render: (_, record) =>
											record?.priorityId
												? priorityOptions?.find(
														(data) => data.id === record.priorityId,
													)?.value
												: '-',
									},
									{
										title: 'Repeat',
										render: (_, record) => {
											if (!record) return '-';
											if (record.repeatFrequencyCustom) return 'Custom';
											return record?.repeatFrequencyId
												? repeatFrequencyOptions?.find(
														(data) => data.id === record.repeatFrequencyId,
													)?.value
												: '-';
										},
									},
									{
										title: 'Jenis Monitoring',
										render: (_, record) =>
											record?.monitoringTypeId
												? monitoringType?.find(
														(data) => data.id === record.monitoringTypeId,
													)?.value
												: '-',
									},
									{
										title: 'Analisa Lebih Lanjut',
										render: (_, record) =>
											record?.furtherAnalysisId
												? furtherAnalysisOptions?.find(
														(data) => data.id === record.furtherAnalysisId,
													)?.value
												: '-',
									},
									{
										title: 'Due Date',
										render: (_, record) =>
											record?.dueDate
												? dayjs(record.dueDate).format('DD/MM/YYYY')
												: '-',
									},
								]}
							/>
						</Form.Item>
					</Fragment>
				)}

				{napApprovalHistory && napApprovalHistory.length ? (
					<Fragment>
						<FleetTitle>Approval History</FleetTitle>
						<Table
							bordered
							loading={isLoading}
							pagination={false}
							dataSource={
								napApprovalHistory?.map((approval, key) => {
									return {
										key,
										...approval,
									};
								}) ?? []
							}
							columns={[
								{
									title: 'No',
									render: (_, _record, index) => index + 1,
								},
								{
									title: 'Name',
									render: (_, record) => record.name,
								},
								{
									title: 'Date',
									render: (_, record) =>
										record.date ? dayjs(record.date).format('DD-MM-YYYY') : '-',
								},
								{
									title: 'Process',
									render: (_, record) => record.process,
								},
								{
									title: 'Status',
									align: 'center',
									render: (_, record) => {
										return (
											<span
												className={`review-status ${record.status.toLowerCase()}`}
											>
												{record.status}
											</span>
										);
									},
								},
								{
									title: 'Approval Notes',
									render: (_, record) => record.notes,
								},
							]}
							style={{ marginBottom: 20 }}
						/>
					</Fragment>
				) : null}

				<Form.Item
					label="Informasi Tambahan"
					initialValue={notes}
					style={{ marginTop: 20 }}
					labelCol={{ span: 4 }}
					name={fieldName('notes')}
					rules={[rule.required()]}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly}
					/>
				</Form.Item>

				{!isReadOnly && (
					<Flex
						justify="end"
						gap={20}
						style={{ marginTop: 40, marginBottom: 40 }}
					>
						<Button
							style={{
								width: 200,
							}}
							disabled={submitRisalah.isPending}
							loading={submitRisalah.isPending}
							onClick={(e) => {
								e.preventDefault();
								navigate('SCRisalahListPage');
							}}
						>
							Back
						</Button>
						<Button
							type="primary"
							htmlType="submit"
							style={{
								width: 200,
							}}
							disabled={submitRisalah.isPending}
							loading={submitRisalah.isPending}
						>
							SAVE
						</Button>
					</Flex>
				)}
			</Form>
		</Fragment>
	);
};

export default SCRisalahPage;
